import { Skeleton } from "@/components/ui/skeleton";
import { useDateFormat, usePublicTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import { CalendarIcon, ClockIcon, MapPinIcon, VideoCameraIcon } from "@phosphor-icons/react";
import { type BookingEvent } from "../../hooks/use-public-booking";

interface CalendarEventAttachmentProps {
  eventId: string;
  events: BookingEvent[] | undefined;
  onEventClick: (event: BookingEvent) => void;
}

export function CalendarEventAttachment({
  eventId,
  events,
  onEventClick,
}: CalendarEventAttachmentProps) {
  const { t } = usePublicTranslation();
  const { format } = useDateFormat();

  // Loading state - events not loaded yet
  if (events === undefined) {
    return (
      <div className="mt-2 border rounded-lg p-3 bg-background/50 min-h-[88px]">
        <div className="flex items-start gap-2">
          <Skeleton className="size-5 rounded" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  // Find the event
  const event = events.find((e) => e.id === eventId);

  // Event not found
  if (!event) {
    return (
      <div className="mt-2 border border-dashed rounded-lg p-3 bg-muted/30 min-h-[88px] flex items-center">
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <CalendarIcon className="size-4" />
          <span>{t("calendar_attachment.event_not_available")}</span>
        </div>
      </div>
    );
  }

  // Event found - render clickable card
  // The conversation's language, not the browser's: a visitor who switched the
  // chat to English read their appointment in French, or the other way round.
  const formatDate = (timestamp: number) => format(timestamp, "EEE d MMM");
  const formatTime = (timestamp: number) => format(timestamp, "p");

  return (
    <button
      onClick={() => onEventClick(event)}
      className={cn(
        "mt-2 w-full border rounded-lg p-3 bg-background/80 min-h-[88px]",
        "hover:bg-background hover:border-primary/50 transition-colors",
        "text-left",
      )}
    >
      <div className="flex items-start gap-3">
        <CalendarIcon className="size-5 text-primary shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <p className="font-medium text-sm line-clamp-1">{event.summary}</p>
          </div>

          <div className="space-y-1 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <ClockIcon className="size-3" />
              <span>
                {formatDate(event.start_time)}
                {!event.is_all_day && ` • ${formatTime(event.start_time)}`}
              </span>
            </div>
            {event.conference_url && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <VideoCameraIcon className="size-3 shrink-0" />
                <span className="line-clamp-1">
                  {t("calendar_event.video")}
                </span>
              </div>
            )}
            {event.location && !event.conference_url && (
              <div className="flex items-center gap-2">
                <MapPinIcon className="size-3" />
                <span className="line-clamp-1">{event.location}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}
